(ns fen.keys
  "The keyboard as a way through the outline. Up and down move the cursor,
  right opens what is closed, left closes what is open - and where there is
  nothing to open or close, right and left step into and out of the nesting
  instead.

  Moving the cursor is the one thing this window does without asking. It
  changes nothing in the graph and nothing another window would want to
  know, and an arrow key that waits for a server is an arrow key that feels
  broken. Folding still asks, because the rows underneath can only come
  from there.

  The rows it walks are the ones on the screen, in the order they are drawn.
  Nesting is drawn and not built (see ento.presenter), so the first child of
  a row is simply the row after it, and the row it hangs under is the
  nearest one above at a smaller depth. No tree has to be reconstructed to
  walk one.

  Space asks what can be done here and hands the keyboard to the popup that
  answers - which is where every action lives now, with a line each saying
  what it really does (see fen.command). Text put up to be read takes the
  keyboard the same way, and gives it back to escape."
  (:require [fen.command :as command]
            [fen.window :as window]))

(defn- rows [] (vec (:rows (:screen @window/WINDOW))))

(defn- index-of [rs h]
  (when h (first (keep-indexed (fn [i r] (when (= h (:handle r)) i)) rs))))

(defn- here
  "Where the cursor stands: the rows, the index in them, and the row itself.
  All three, because every key needs at least two of them."
  []
  (let [rs (rows)
        i  (index-of rs (:cursor (window/view)))]
    [rs i (when i (nth rs i nil))]))

(defn- go!
  "Move the cursor there, and make sure it can be seen. The row is found in
  the document by its handle, because that is what it is called on both
  sides - and a cursor that has scrolled out of the window is one nobody
  can follow."
  [h]
  (when h
    (window/cursor! h)
    (js/requestAnimationFrame
     #(some-> (.querySelector js/document (str "[data-handle='" h "']"))
              (.scrollIntoView #js {:block "nearest"})))))

(defn- move!
  "One row further. With no cursor yet, down takes the first row and up the
  last, so the first keystroke always lands somewhere."
  [delta]
  (let [[rs i _] (here)
        n        (count rs)]
    (when (pos? n)
      (go! (:handle (nth rs (if (nil? i)
                              (if (pos? delta) 0 (dec n))
                              (min (dec n) (max 0 (+ i delta))))))))))

(defn- deeper!
  "Right: open what is closed, step into what is already open, and leave a
  row that has nothing under it alone."
  []
  (let [[_ _ row] (here)]
    (cond
      (nil? row)      (move! 1)
      (not (:foldable? row)) nil
      (:folded? row)  (window/send! {:type :unfold :handle (:handle row)})
      :else           (move! 1))))

(defn- parent-of
  "The row this one hangs under: the nearest one above it at a smaller
  depth. nil at the top level, where there is nothing to step out to."
  [rs i]
  (let [d (or (:depth (nth rs i)) 0)]
    (when (pos? d)
      (some #(when (< (or (:depth %) 0) d) %) (reverse (subvec rs 0 i))))))

(defn- shallower!
  "Left: close what is open, and otherwise step out to the row this one
  hangs under."
  []
  (let [[rs i row] (here)]
    (cond
      (nil? row)                                  (move! -1)
      (and (:foldable? row) (not (:folded? row))) (window/send! {:type :fold :handle (:handle row)})
      :else                                       (go! (:handle (parent-of rs i))))))

(defn- commands!
  "Space: what can be done here. On the row the cursor stands on - and with
  the cursor nowhere yet, on the entity itself, whose own actions the popup
  carries either way."
  []
  (let [[_ _ row] (here)]
    (window/open-menu! (:handle row))))

(defn- enter!
  "Enter is the keyboard's tap: what the row does when it is pressed. A
  row that can fold folds, every other row asks what can be done with it -
  the same two answers a finger gets (see fen.screen/Row)."
  []
  (let [[_ _ row] (here)]
    (cond
      (nil? row)      (commands!)
      (:foldable? row) (window/send! {:type (if (:folded? row) :unfold :fold)
                                      :handle (:handle row)})
      :else           (commands!))))

(def ^:private keys->act
  {"ArrowDown"  #(move! 1)
   "ArrowUp"    #(move! -1)
   "ArrowRight" deeper!
   "ArrowLeft"  shallower!
   "Enter"      enter!
   " "          commands!})

(defn- offers [] (command/offered (:screen @window/WINDOW) (window/menu)))

(defn- pick!
  "One offer further down the popup, and round the end - the list is short
  enough that walking off the bottom means the top."
  [delta]
  (let [n (count (offers))]
    (when (pos? n)
      (window/menu-index! (mod (+ (:index (window/menu)) delta) n)))))

(defn- menu-key
  "What a keystroke does while the popup is up, or nil when it does nothing
  and the browser may keep it.

  The thunk is handed back rather than run, so the keystroke can be taken
  away from the page before anything opens a prompt on top of it. An action's
  own letter runs it straight away: the descriptions are there to be read the
  first few times and skipped ever after."
  [k]
  (let [{:keys [index]} (window/menu)
        offers          (offers)]
    (case k
      ("Escape" " ") window/close-menu!
      "ArrowDown"    #(pick! 1)
      "ArrowUp"      #(pick! -1)
      "Enter"        (when-let [o (nth offers index nil)] #(command/act! o))
      (when-let [o (first (filter #(= k (:key (:action %))) offers))]
        #(command/act! o)))))

(defn- shown-key
  "What a keystroke does while there is something up to be read: escape,
  space and enter close it, and nothing else is taken.

  Nothing else on purpose. The text can be longer than the box it is in, so
  page down and the arrows scrolling it is worth more than them moving a
  cursor nobody can see underneath (see fen.screen/Shown)."
  [k]
  (when (#{"Escape" " " "Enter"} k)
    window/close-show!))

(defn- ask-key
  "What a keystroke does while an action is asking for something. Escape is
  no; enter is yes, but only for a question with no field - in a field
  enter is the field's, and the field says when it means send
  (see fen.screen/Ask)."
  [k]
  (case k
    "Escape" command/cancel!
    "Enter"  (let [offer (window/ask)]
               (when (= :confirm (:type (:input (:action offer))))
                 #(command/answer! offer nil)))
    nil))

(defn- typing?
  "Whether the keystroke belongs to something else. The sign-in field is a
  text field, and an arrow key in a text field means what it has always
  meant."
  []
  (boolean (some-> js/document.activeElement
                   .-tagName
                   #{"INPUT" "TEXTAREA" "SELECT"})))

(defn- on-key
  "Whatever is on top has the keyboard for as long as it is up, and the
  outline underneath hears nothing - which is what makes Escape worth
  reaching for."
  [^js e]
  (when-not (or (typing?) (.-altKey e) (.-ctrlKey e) (.-metaKey e) (.-shiftKey e))
    (when-let [f (cond
                   (window/shown) (shown-key (.-key e))
                   (window/ask)   (ask-key (.-key e))
                   (window/menu)  (menu-key (.-key e))
                   :else          (get keys->act (.-key e)))]
      (.preventDefault e)
      (f))))

(defonce ^:private !installed (atom false))

(defn install!
  "One listener on the document, once. On the document rather than on a row,
  because nothing here is focused and there is nothing to focus - the cursor
  is a place in the screen, not a place in the browser."
  []
  (when (compare-and-set! !installed false true)
    (.addEventListener js/document "keydown" on-key)))
